import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtGBP, fmtMoney, statusTone } from "@/lib/format";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, XCircle, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const METRICS = [
  ["total_imported", "Total Imported", "text-slate-100"],
  ["total_included", "In Dataset", "text-amber-400"],
  ["sold", "Sold", "text-emerald-400"],
  ["unsold", "Unsold", "text-red-400"],
  ["below_cap", "Below Cap", "text-slate-200"],
  ["above_cap", "Above Cap", "text-slate-200"],
  ["missing_prices", "Missing Prices", "text-amber-300"],
  ["unknown_price_basis", "Unknown Basis", "text-amber-300"],
  ["missing_artist", "Missing Artist", "text-amber-300"],
  ["missing_currency_rate", "Missing FX Rate", "text-amber-300"],
  ["published_unsold_bids", "Published Bids", "text-cyan-400"],
  ["duplicate_candidates", "Dup Candidates", "text-violet-400"],
  ["failed_imports", "Failed Imports", "text-red-400"],
  ["requiring_review", "Needs Review", "text-violet-400"],
];

export default function DataQuality() {
  const qc = useQueryClient();
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ reason: "", corrected_artist: "", corrected_category: "", confirmed_price_basis: "" });

  const { data: m } = useQuery({ queryKey: ["quality"], queryFn: async () => (await api.get("/dashboard/quality")).data });
  const { data: queue = [] } = useQuery({ queryKey: ["review-queue"], queryFn: async () => (await api.get("/review/queue")).data });

  const submit = async (decision) => {
    if (!form.reason.trim()) { toast.error("A decision reason is required"); return; }
    try {
      await api.post(`/review/${encodeURIComponent(active.lot_id)}`, { decision, ...form });
      toast.success(`Record ${decision}d`);
      setActive(null);
      setForm({ reason: "", corrected_artist: "", corrected_category: "", confirmed_price_basis: "" });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["quality"] });
    } catch { toast.error("Action failed"); }
  };

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Governance</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
        <ShieldAlert className="w-7 h-7 text-amber-400" /> Data Quality & Review
      </h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-6">
        {m && METRICS.map(([k, label, tone]) => (
          <div key={k} data-testid={`metric-${k}`} className="al-card p-4">
            <p className={`mono-num text-2xl ${tone}`}>{m[k]}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-2xl mt-10 mb-4">Review Queue <span className="mono-num text-amber-400 text-lg">({queue.length})</span></h2>
      <div className="al-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="review-queue-table">
            <thead><tr className="border-b border-slate-800 bg-[#0E131F] text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">Artist</th><th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">House</th>
              <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Result</th><th className="px-4 py-2.5">Reason</th><th className="px-4 py-2.5 text-right">Action</th>
            </tr></thead>
            <tbody>
              {queue.map((l, i) => (
                <tr key={l.lot_id} data-testid={`review-row-${i}`} className="border-b border-slate-800/50">
                  <td className="px-4 py-3 font-serif text-amber-400">{l.artist}</td>
                  <td className="px-4 py-3 text-slate-300 max-w-[160px] truncate">{l.title}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{l.auction_house}</td>
                  <td className="px-4 py-3"><span className={`al-chip ${statusTone[l.status]}`}>{l.status}</span></td>
                  <td className="px-4 py-3 mono-num text-xs text-slate-300">{fmtMoney(l.result_price, l.currency)} {l.result_price_gbp ? `(${fmtGBP(l.result_price_gbp)})` : ""}</td>
                  <td className="px-4 py-3">
                    {l.data_source === "ai_daily_scan" && <span className="al-chip bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 mr-1">AI</span>}
                    <span className="al-chip bg-violet-500/15 text-violet-300 border border-violet-500/30">{l.review_reason}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`review-open-${i}`} onClick={() => setActive(l)} className="al-btn-ghost text-xs"><Pencil className="w-3.5 h-3.5" /> Inspect</button>
                  </td>
                </tr>
              ))}
              {queue.length === 0 && <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500">Queue empty — all records reviewed.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="review-dialog" className="bg-[#121824] border border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Review record</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="al-card p-4 text-sm space-y-1">
                <p className="font-serif text-amber-400 text-lg">{active.artist} — {active.title}</p>
                <p className="text-xs text-slate-400 mono-num">{active.lot_id}</p>
                <p className="text-xs text-slate-400">Original source name: <span className="text-slate-200">{active.original_artist_name}</span></p>
                <p className="text-xs text-slate-400">Native result: <span className="text-slate-200 mono-num">{fmtMoney(active.result_price, active.currency)}</span> · GBP: <span className="mono-num">{fmtGBP(active.result_price_gbp)}</span></p>
                <p className="text-xs text-violet-300">Flags: {active.review_reason}</p>
                {active.lot_url && <a href={active.lot_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 underline">View source →</a>}
                {active.data_source === "ai_daily_scan" && (
                  <p className="text-xs text-cyan-300 mt-1">AI-sourced candidate — verify before approving.{active.source_url && <> <a href={active.source_url} target="_blank" rel="noreferrer" className="underline">AI source →</a></>}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="al-label">Correct artist (opt.)</label><input data-testid="review-artist-input" className="al-input" value={form.corrected_artist} onChange={(e) => setForm({ ...form, corrected_artist: e.target.value })} placeholder={active.artist} /></div>
                <div><label className="al-label">Correct category (opt.)</label><input data-testid="review-category-input" className="al-input" value={form.corrected_category} onChange={(e) => setForm({ ...form, corrected_category: e.target.value })} placeholder={active.category || ""} /></div>
              </div>
              <div>
                <label className="al-label">Confirm price basis (opt.)</label>
                <select data-testid="review-basis-select" className="al-input" value={form.confirmed_price_basis} onChange={(e) => setForm({ ...form, confirmed_price_basis: e.target.value })}>
                  <option value="">— leave as is —</option><option value="hammer_price">Hammer price</option><option value="price_including_buyers_premium">Incl. buyer's premium</option>
                </select>
              </div>
              <div><label className="al-label">Decision reason *</label><textarea data-testid="review-reason-input" className="al-input" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Record why you approved / corrected / rejected" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="review-reject-button" onClick={() => submit("reject")} className="al-btn-ghost text-red-400 border-red-500/40"><XCircle className="w-4 h-4" /> Reject</button>
            <button data-testid="review-correct-button" onClick={() => submit("correct")} className="al-btn-ghost"><Pencil className="w-4 h-4" /> Save correction</button>
            <button data-testid="review-approve-button" onClick={() => submit("approve")} className="al-btn"><CheckCircle2 className="w-4 h-4" /> Approve</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
